import * as React from 'react'
import type { HoneybookConnectionDto } from '@lib/dto/honeybook.dto'
import { formatDate } from '@lib/helpers/format'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { useAuth } from '@/auth/AuthContext'
import { admits } from '@/auth/access'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCancelHoneybookSwitch, useStartHoneybookSwitch } from '@/hooks/useHoneyBook'
import { todayIso } from '@/lib/bookedDays'
import { instantLabel } from '@/lib/instantLabel'

const STAYS_BEHIND =
  'Contracts, invoices, payment history, messages and files stay in HoneyBook. Couples already booked keep paying there until their events are over. New bookings are made here, with this venue’s own proposals, contracts and payments.'

function Title() {
  return <h3 className="m-0 text-sm font-semibold text-ink">Switch to FLOH Venues</h3>
}

/** Choosing the day, after saying what stays behind and that it can't be undone. */
function StartSwitch({ venueId }: { venueId: string }) {
  const start = useStartHoneybookSwitch(venueId)
  const [open, setOpen] = React.useState(false)
  const [day, setDay] = React.useState<CalendarDay | ''>('')
  return (
    <div>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        Switch to FLOH Venues…
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Switch this venue to FLOH Venues?</DialogTitle>
            <DialogDescription>
              On the day you choose, HoneyBook is read one last time. Its open inquiries become inquiries here, and the events it manages become the
              venue’s own. This can’t be undone.
            </DialogDescription>
          </DialogHeader>
          <p className="m-0 text-sm text-ink-muted">{STAYS_BEHIND}</p>
          <Field label="Switch on" htmlFor="honeybook-switch-on" hint="At 2 AM the venue’s time that day; today, within the hour.">
            <DatePicker id="honeybook-switch-on" value={day} onChange={setDay} min={todayIso()} placeholder="Choose a day" />
          </Field>
          {start.isError ? <FormError>{requestErrorMessage(start.error)}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Not now
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={day === '' || start.isPending}
              onClick={() => {
                if (day !== '') start.mutate({ cutoverDate: day }, { onSuccess: () => setOpen(false) })
              }}
            >
              {start.isPending ? 'Setting…' : day === '' ? 'Switch' : `Switch on ${formatDate(day)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** A switch set for a day: says when, and lets someone who may switch call it off. */
function PendingSwitch({ venueId, on, canSwitch }: { venueId: string; on: CalendarDay; canSwitch: boolean }) {
  const cancel = useCancelHoneybookSwitch(venueId)
  const [confirming, setConfirming] = React.useState(false)
  return (
    <div className="space-y-3">
      <p className="m-0 text-sm text-ink">Switching on {formatDate(on)}, from 2 AM the venue’s time.</p>
      <p className="m-0 text-sm text-ink-muted">{STAYS_BEHIND}</p>
      {canSwitch ? (
        <>
          <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
            Call off the switch
          </Button>
          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title="Call off the switch?"
            description="The venue stays on HoneyBook, read every hour as now. You can choose another day later."
            confirmLabel="Call it off"
            busy={cancel.isPending}
            error={cancel.isError ? requestErrorMessage(cancel.error) : null}
            onConfirm={() => cancel.mutate(undefined, { onSuccess: () => setConfirming(false) })}
          />
        </>
      ) : null}
    </div>
  )
}

/**
 * Switching a venue from HoneyBook to FLOH Venues (docs/honeybook.plan.md,
 * phase 7), one way: offered to someone who sets up integrations across the
 * company once the venue's own proposals, contracts and payments are set up;
 * otherwise it says why not.
 */
export function HoneyBookSwitch({ venueId, connection }: { venueId: string; connection: HoneybookConnectionDto }) {
  const { user } = useAuth()
  const canSwitch = admits(user, { staff: 'integrations:edit', companyWide: true })
  if (connection.switchedAt !== null) {
    // The status already says it switched; this says when, and what that leaves.
    return (
      <p className="m-0 text-sm text-ink-muted">
        Switched {instantLabel(connection.switchedAt)}. HoneyBook isn’t read any more; what it recorded still counts for the periods before.
      </p>
    )
  }
  if (connection.switchOn !== null) {
    return (
      <section className="space-y-2">
        <Title />
        <PendingSwitch venueId={venueId} on={connection.switchOn} canSwitch={canSwitch} />
      </section>
    )
  }
  if (!connection.switchOffered && connection.switchBlockedBy === null) return null
  return (
    <section className="space-y-2">
      <Title />
      <p className="m-0 text-sm text-ink-muted">Run this venue’s inquiries, bookings, proposals, contracts and payments here instead of in HoneyBook.</p>
      {connection.switchOffered ? <StartSwitch venueId={venueId} /> : <p className="m-0 text-sm text-ink-muted">{connection.switchBlockedBy}</p>}
    </section>
  )
}
